import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, AlertTriangle, Clock, Bell, FileText, ChevronLeft, ChevronRight, Database, LogOut, User, Pencil } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../contexts/LanguageContext";

const navItemsOld = null; // removed - now inside component

export default function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLang();
  const [collapsed, setCollapsed] = useState(false);

  const navItems = [
    { path: "/dashboard", label: t("dashboard.overview"), icon: LayoutDashboard, exact: true },
    { path: "/dashboard/recalls", label: t("dashboard.recalls"), icon: AlertTriangle },
    { path: "/dashboard/dlc", label: t("dashboard.dlcNotifs"), icon: Clock },
    { path: "/dashboard/alerts", label: t("dashboard.alertsSent"), icon: Bell },
    { path: "/dashboard/compliance", label: t("dashboard.compliance"), icon: FileText },
    { path: "/dashboard/purchases", label: t("dashboard.purchases"), icon: Database },
    { path: "/dashboard/editor", label: "Editeur textes", icon: Pencil },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  return (
    <div data-testid="dashboard-layout" className="flex min-h-screen" style={{ paddingTop: 60 }}>
      {/* Sidebar */}
      <aside
        data-testid="dashboard-sidebar"
        className="sticky top-[60px] h-[calc(100vh-60px)] flex flex-col transition-all duration-200 shrink-0 border-r"
        style={{
          width: collapsed ? 64 : 240,
          background: "var(--kyra-gm)",
          borderColor: "rgba(255,255,255,0.08)",
        }}
      >
        <div className="flex items-center justify-between px-4 py-4">
          {!collapsed && (
            <span className="text-xs font-medium tracking-widest uppercase" style={{ color: "rgba(183,228,199,0.5)" }}>
              Dashboard
            </span>
          )}
          <button
            data-testid="sidebar-toggle"
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded hover:bg-white/10 transition-colors cursor-pointer"
            style={{ color: "rgba(255,255,255,0.5)", border: "none", background: "none" }}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
        <nav className="flex flex-col gap-1 px-2 flex-1">
          {navItems.map((item) => {
            const active = isActive(item);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.split("/").pop() || "overview"}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md no-underline transition-all text-[13px] font-normal"
                style={{
                  background: active ? "rgba(255,255,255,0.12)" : "transparent",
                  color: active ? "#fff" : "rgba(255,255,255,0.5)",
                }}
                title={collapsed ? item.label : undefined}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>
        {/* User + Logout */}
        <div className="px-2 mt-auto mb-2">
          {!collapsed && user && (
            <div className="px-3 py-2 mb-1 rounded-md text-[11px] font-light truncate" style={{ color: "rgba(255,255,255,0.5)" }}>
              <User className="w-3 h-3 inline mr-1.5" />{user.name || user.email}
            </div>
          )}
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-md w-full text-[13px] transition-all cursor-pointer"
            style={{ background: "rgba(176,48,37,0.15)", color: "#EF9A9A", border: "none" }}
            title="Deconnexion"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            {!collapsed && <span>{t("dashboard.logout")}</span>}
          </button>
        </div>
        {!collapsed && (
          <div className="px-4 py-3 text-[10px] font-light" style={{ color: "rgba(255,255,255,0.2)" }}>
            KYRA v1.0 &middot; Beta
          </div>
        )}
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 p-6 md:p-8" style={{ background: "var(--kyra-bg)" }}>
        <Outlet />
      </main>
    </div>
  );
}
